import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Input } from "@shared/ui/input";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Filter, Search } from "lucide-react";
import React, { useState } from "react";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../../shared/ui/card";
import {
  useBillCycleMeterReading,
  useMeterTypeChoices,
  useUtilityServices,
} from "../../hooks";

interface MeterListTabProps {
  billCycleId: number;
  billDraftId: number;

}

export const MeterListTab: React.FC<MeterListTabProps> = ({ billCycleId,billDraftId }) => {
  const itemsPerPage = PAGE_SIZE;
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  console.log("Bill Cycle ID innn:", billCycleId);

  const [queryParams, setQueryParams] = useState<Record<string, any>>({
    page: currentPage,
    search_data: undefined,
    meter_type: undefined,
    service: undefined,
    status: undefined,
    bill_cycle_id: billCycleId,
    bill_draft_id:billDraftId
  });

  const {
    data: meterReadingList,
    isPending: isMeterReadingLoading,
    error: meterReadingError,
  } = useBillCycleMeterReading(queryParams);
  const { data: meterTypes } = useMeterTypeChoices();
  const { data: utilityServices } = useUtilityServices();
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQueryParams((prev) => ({
      ...prev,
      search_data: value || undefined,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setQueryParams((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const filterFields: FormField[] = [
    {
      name: "meter_type",
      label: "All Meter Types",
      type: "select",
      placeholder: "Select Status",
      className: "col-span-1",
      options: meterTypes?.map((service: any) => ({
        value: service.id,
        label: service.name,
      })),
      fullWidth: true,
      
    },
    {
      name: "product_code",
      label: "All Services",
      type: "select",
      placeholder: "Select Type",
      className: "col-span-1",
      options: utilityServices?.map((service: any) => ({
        value: service.id,
        label: service.name,
      })),
      fullWidth: true,
      
    },
    {
      name: "type",
      label: "All Statuses",
      type: "select",
      placeholder: "Select Type",
      className: "col-span-1",
      options: [],
      fullWidth: true,
      
    },
  ];

  const filterForm = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const clearFilters = () => {
    filterForm.reset();
    setQueryParams((prev) => ({
      ...prev,
      status: undefined,
      meter_type: undefined,
      product_code: undefined,
      page: 1,
    }));
    setShowFilters(!showFilters);
  };

  const applyFilters = async () => {
    const filters = filterForm.getValues();
    setQueryParams((prev) => ({
      ...prev,
      ...filters,
      page: 1,
    }));
  };

  
  const columns = [
    { key: "meterNumber", header: "Meter No" },
    { key: "consumerName", header: "Consumer" },
    { key: "meterType", header: "Meter Type" },
    { key: "productCode", header: "Service", },
    { key: "previousReading", header: "Last Reading", },
    {
      key: "currentMeterReading",
      header: "Current Reading"
    },
    { key: "consumption", header: "Consumption" },
    { key: "validationStatus", header: "Status" },
  ];

  return (
    <div className="space-y-4">
      <Card className="transition-all duration-300 hover:shadow-lg hover:scale-102 focus-within:shadow-xl focus-within:ring-2 focus-within:ring-accent">
        <CardHeader>
          <CardTitle>Meter Reading List</CardTitle>
          <CardDescription>
            All meters in the current billing cycle
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by meter ID, account, or consumer name"
                className="pl-8"
                onChange={handleSearchChange}
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 border rounded-md text-sm font-medium hover:bg-gray-100"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="flex flex-col gap-3 justify-start items-start border p-4 rounded-md mb-4 w-full">
              <DynamicForm
                fields={filterFields}
                form={filterForm}
                onReset={clearFilters}
                onSubmit={applyFilters}
                gridColumns={3}
                config={{
                  showResetButton: true,
                  showSubmitButton: true,
                  resetButtonText: "Clear All",
                  submitButtonText: "Apply",
                  classes: {
                    form: "gap-3 w-full",
                    buttonContainer:
                      "flex justify-end gap-2 col-span-full",
                  },
                }}
              />
            </div>
          )}

          <div className="overflow-auto max-h-[500px] border rounded-md">
            <GenericTable
              data={meterReadingList?.results ?? []}
              columns={columns}
              rowKey="id"
              actionsType="dropdown"
              emptyMessage="No meters found"
              loading={isMeterReadingLoading}
              pagination={{
                currentPage: currentPage,
                totalPages: Math.ceil(
                  (meterReadingList?.count || 0) / itemsPerPage
                ),
                totalItems: meterReadingList?.count || 0,
                itemsPerPage: itemsPerPage,
                startItem: (currentPage - 1) * itemsPerPage + 1,
                endItem: Math.min(
                  currentPage * itemsPerPage,
                  meterReadingList?.count || 0
                ),
              }}
              onPageChange={handlePageChange}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
